import { AlertTriangle, Clock, CheckCircle2 } from "lucide-react";

/**
 * Calcola i giorni residui fino a data_scadenza (differenza in giorni interi, tz-safe).
 * Ritorna intero: >0 giorni residui, 0 oggi, <0 giorni scaduti.
 */
export function daysUntil(data_scadenza) {
  if (!data_scadenza) return null;
  const scad = new Date(data_scadenza);
  if (isNaN(scad.getTime())) return null;
  const ms = new Date(scad.setHours(0, 0, 0, 0)).getTime() - new Date(new Date().setHours(0, 0, 0, 0)).getTime();
  return Math.round(ms / 86400000);
}

/**
 * Ritorna { tone, text, Icon } per una data di scadenza.
 * tone: 'red' | 'amber' | 'green' | 'gray'
 */
export function scadenzaInfo(data_scadenza) {
  const g = daysUntil(data_scadenza);
  if (g === null) return { tone: "gray", text: "—", Icon: Clock };
  if (g < 0) return { tone: "red", text: `Scaduto da ${Math.abs(g)}${Math.abs(g) === 1 ? "g" : "g"}`, Icon: AlertTriangle };
  if (g === 0) return { tone: "red", text: "Scade oggi", Icon: AlertTriangle };
  if (g <= 3) return { tone: "amber", text: `${g}g`, Icon: Clock };
  return { tone: "green", text: `${g}g`, Icon: CheckCircle2 };
}

const TONE_CLASSES = {
  red: "bg-red-100 text-red-800 border-red-300",
  amber: "bg-amber-100 text-amber-800 border-amber-300",
  green: "bg-green-100 text-green-800 border-green-300",
  gray: "bg-slate-50 text-slate-600 border-slate-200",
};

/**
 * Badge compatto per liste (tickets/preventivi). Countdown giorni residui/scaduti.
 */
export function ScadenzaBadge({ data_scadenza, testid = "scadenza-badge" }) {
  if (!data_scadenza) return null;
  const { tone, text, Icon } = scadenzaInfo(data_scadenza);
  return (
    <span data-testid={testid}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold border ${TONE_CLASSES[tone]}`}>
      <Icon className="w-3 h-3" /> {text}
    </span>
  );
}

/**
 * Banner grande da mettere in cima al dettaglio di una pratica.
 * Etichetta contestuale in base allo stato/tipo pratica.
 */
export function ScadenzaBanner({ data_scadenza, label = "Termine", testid = "scadenza-banner" }) {
  if (!data_scadenza) return null;
  const g = daysUntil(data_scadenza);
  if (g === null) return null;
  let tone = "green", testo = "";
  if (g < 0) { tone = "red"; testo = `Scaduto da ${Math.abs(g)} ${Math.abs(g) === 1 ? "giorno" : "giorni"}`; }
  else if (g === 0) { tone = "red"; testo = "Scade oggi"; }
  else if (g <= 3) { tone = "amber"; testo = g === 1 ? "Termine tra 1 giorno" : `Termine tra ${g} giorni`; }
  else { tone = "green"; testo = `Termine tra ${g} giorni`; }
  const bannerStyles = {
    red:   { border: "border-red-700",   bg: "bg-red-50",   ic: "text-red-700",   tx: "text-red-900",   Icon: AlertTriangle },
    amber: { border: "border-amber-500", bg: "bg-amber-50", ic: "text-amber-600", tx: "text-amber-900", Icon: Clock },
    green: { border: "border-green-600", bg: "bg-green-50", ic: "text-green-700", tx: "text-green-900", Icon: Clock },
  }[tone];
  const IconC = bannerStyles.Icon;
  const dataFmt = new Date(data_scadenza).toLocaleString("it-IT", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div data-testid={testid}
      className={`mb-5 p-4 border-l-8 rounded-r-lg flex items-center gap-3 ${bannerStyles.border} ${bannerStyles.bg}`}>
      <IconC className={`w-7 h-7 shrink-0 ${bannerStyles.ic}`} />
      <div className={`${bannerStyles.tx} flex-1`}>
        <div className="font-bold text-lg" data-testid={`${testid}-testo`}>{testo}</div>
        <div className="text-sm opacity-90">{label}: <b>{dataFmt}</b></div>
      </div>
    </div>
  );
}

